import { useEffect, useMemo, useState } from 'react';
import {
  DEPARTURE_PAGING_DEFAULT,
  DEPARTURE_TIME_FORMAT_DEFAULT,
  DEPARTURES_TIME_SPAN_DEFAULT,
  MILLISECONDS_BETWEEN_ALIVE_MESSAGES,
  MILLISECONDS_BETWEEN_EVENT_SOURCE_RECONNECTION_ATTEMPTS,
  MILLISECONDS_OF_MAX_EXPECTED_SERVER_STARTUP_TIME,
  MILLISECONDS_OF_MAXIMUM_EXPECTED_LATENCY,
} from '@shared/constants';
import { VERSION } from '../../version';
import { dateReviver } from '@shared/utils/dateReviver';
import { BoardConfiguration } from '@shared/models/boardConfiguration';
import { ResourcesSet } from '@shared/models/events/resourcesSet';
import { isBoardEvent } from '@shared/utils/isBoardEvent';
import useQueryParameters from '@shared/hooks/useQueryParameters';
import { BoardEvent } from '@shared/models/boardEvent';
import { ResourcesAdded } from '@shared/models/events/resourcesAdded';
import { ResourcesUpdated } from '@shared/models/events/resourcesUpdated';
import { ResourcesRemoved } from '@shared/models/events/resourcesRemoved';
import { BoardId } from '@shared/models/boardId';
import { logError } from '@shared/utils/errorLogging';
import { ResourceSet } from '@shared/models/events/resourceSet';
import { ResourceUpdated } from '@shared/models/events/resourceUpdated';
import { ResourceName } from '@shared/models/resourceName';
import { ResourcesName } from '@shared/models/resourcesName';
import { stopHeaderSlice, journeySlice } from '@shared/resourceSlices';
import { useAppDispatch, useAppSelector } from '@shared/store';
import { aliveEventReceived } from '@shared/serverSentEventsSlice';
import { ResourcesType } from '@shared/models/resourcesType';
import { ResourceType } from '@shared/models/resourceType';
import {
  arrivalRowsSlice,
  departureRowsSlice,
  trafficSituationsSlice,
  extendedMessagesSlice,
} from '@shared/resourcesSlices';
import { isServiceJourney } from '@shared/utils/gid';
import { DeepReadonly } from 'ts-essentials';

interface VehicleServerSentEventsConnectionParameters {
  journeyGid: string;
  journeyStartTime: Date;
  type: string | undefined;
  regNo: string | undefined;
  vehicleNo: string | undefined;
  xid: string | undefined;
  operator: string | undefined;
}

export function createServerSentEventsUrl(
  boards: BoardConfiguration[],
  vehicleConnectionParameters?: DeepReadonly<VehicleServerSentEventsConnectionParameters>,
  id?: string | null,
): string | undefined {
  function addGid(
    urlSearchParams: URLSearchParams,
    boardId: BoardId,
    gid: string,
  ) {
    urlSearchParams.append(`board${boardId}Gids`, gid);
  }

  const eventsUrlSearchParams = new URLSearchParams();
  for (const board of boards) {
    // Identifiers
    if (board.type === 'stop-area') {
      addGid(eventsUrlSearchParams, board.id, board.stopAreaGid);
    } else if (board.type === 'stop-points') {
      for (const stopPointGid of board.stopPointGids) {
        addGid(eventsUrlSearchParams, board.id, stopPointGid);
      }
    } else if (
      board.type === 'vehicle' &&
      vehicleConnectionParameters &&
      isServiceJourney(vehicleConnectionParameters.journeyGid)
    ) {
      eventsUrlSearchParams.append(
        `board${board.id}ServiceJourneyGid`,
        vehicleConnectionParameters.journeyGid,
      );
      eventsUrlSearchParams.append(
        `board${board.id}ServiceJourneyStartTime`,
        vehicleConnectionParameters.journeyStartTime.toISOString(),
      );
    } else if (board.type !== 'information') {
      continue;
    }

    // Modules
    for (const module of board.modules) {
      eventsUrlSearchParams.append(`board${board.id}Modules`, module);
    }

    // Configuration
    if (board.type === 'stop-area' || board.type === 'stop-points') {
      if (
        board.departurePaging !== undefined &&
        board.departurePaging !== DEPARTURE_PAGING_DEFAULT
      ) {
        eventsUrlSearchParams.append(
          `board${board.id}DeparturePaging`,
          board.departurePaging.toString(),
        );
      }
      if (
        board.departureTimeFormat !== undefined &&
        board.departureTimeFormat !== DEPARTURE_TIME_FORMAT_DEFAULT
      ) {
        eventsUrlSearchParams.append(
          `board${board.id}DepartureTimeFormat`,
          board.departureTimeFormat,
        );
      }
      if (
        board.departureTimeSpan !== undefined &&
        board.departureTimeSpan !== DEPARTURES_TIME_SPAN_DEFAULT
      ) {
        eventsUrlSearchParams.append(
          `board${board.id}DepartureTimeSpan`,
          board.departureTimeSpan,
        );
      }
    }

    // Statistics
    if (board.type === 'vehicle' && vehicleConnectionParameters) {
      if (vehicleConnectionParameters.type) {
        eventsUrlSearchParams.append(
          `board${board.id}VehicleType`,
          vehicleConnectionParameters.type,
        );
      }
      if (vehicleConnectionParameters.regNo) {
        eventsUrlSearchParams.append(
          `board${board.id}VehicleRegNo`,
          vehicleConnectionParameters.regNo,
        );
      }
      if (vehicleConnectionParameters.vehicleNo) {
        eventsUrlSearchParams.append(
          `board${board.id}VehicleNo`,
          vehicleConnectionParameters.vehicleNo,
        );
      }
      if (vehicleConnectionParameters.xid) {
        eventsUrlSearchParams.append(
          `board${board.id}VehicleXid`,
          vehicleConnectionParameters.xid,
        );
      }
      if (vehicleConnectionParameters.operator) {
        eventsUrlSearchParams.append(
          `board${board.id}VehicleOperator`,
          vehicleConnectionParameters.operator,
        );
      }
    }
  }

  if (!eventsUrlSearchParams.toString()) {
    return;
  }

  if (id) {
    eventsUrlSearchParams.append('id', id);
  }

  return `api/events?${eventsUrlSearchParams.toString()}`;
}

export function useServerSentEvents(boards: BoardConfiguration[] | undefined) {
  const dispatch = useAppDispatch();
  const [connectionErrorCount, setConnectionErrorCount] = useState(0);
  const id = useQueryParameters().get('id');
  const vehicleJourneyGid = useAppSelector((state) => state.vehicle.journeyGid);
  const vehicleJourneyStartTime = useAppSelector(
    (state) => state.vehicle.journeyStartTime,
  );
  const vehicleType = useAppSelector((state) => state.vehicle.type);
  const vehicleRegNo = useAppSelector((state) => state.vehicle.regNo);
  const vehicleNo = useAppSelector((state) => state.vehicle.vehicleNo);
  const vehicleXid = useAppSelector((state) => state.vehicle.xid);
  const vehicleOperator = useAppSelector((state) => state.vehicle.operator);
  const vehicleConnectionParameters = useMemo(() => {
    if (!vehicleJourneyGid || !vehicleJourneyStartTime) {
      return undefined;
    }

    const journeyStartTime = new Date(vehicleJourneyStartTime * 1000); // Convert from Unix time

    return {
      journeyGid: vehicleJourneyGid,
      journeyStartTime,
      type: vehicleType,
      regNo: vehicleRegNo,
      vehicleNo,
      xid: vehicleXid,
      operator: vehicleOperator,
    };
  }, [
    vehicleJourneyGid,
    vehicleJourneyStartTime,
    vehicleNo,
    vehicleOperator,
    vehicleRegNo,
    vehicleType,
    vehicleXid,
  ]);

  useEffect(() => {
    if (!boards || boards.length === 0) {
      return;
    }

    let hasMatchingVersion = false;

    let lastAliveMessageTime = Date.now();

    let retryConnectionTimerId: number | undefined;

    function retryConnection() {
      setConnectionErrorCount((previousErrorCount) => previousErrorCount + 1);
    }

    function closeWaitAndRetryConnection(error: unknown) {
      console.warn(
        `Resetting connection in ${
          MILLISECONDS_BETWEEN_EVENT_SOURCE_RECONNECTION_ATTEMPTS / 1000
        } seconds due to error`,
      );

      logError(error);

      eventSource.close();

      setRetryConnectionTimer();
    }

    function setRetryConnectionTimer() {
      if (retryConnectionTimerId) {
        return;
      }

      retryConnectionTimerId = window.setTimeout(() => {
        retryConnection();
        retryConnectionTimerId = undefined;
      }, MILLISECONDS_BETWEEN_EVENT_SOURCE_RECONNECTION_ATTEMPTS);
    }

    function getEvent<TEvent>(data: unknown): TEvent | undefined {
      if (!data || typeof data !== 'string') {
        return undefined;
      }
      try {
        const event: unknown = JSON.parse(data, dateReviver);
        return isBoardEvent<TEvent>(event) ? event : undefined;
      } catch (error) {
        logError(error);
        return undefined;
      }
    }

    function handleBoardEvent<TEvent extends BoardEvent>(
      e: MessageEvent<unknown>,
      eventHandler: (event: TEvent) => void,
    ): void {
      // Ignore events on unmatched version to avoid acting on potentially incompatible events before reloading
      if (!hasMatchingVersion) {
        return;
      }
      const event = getEvent<TEvent>(e.data);
      if (!event) {
        closeWaitAndRetryConnection('Failed to parse event');
        return;
      }

      eventHandler(event);
    }

    function registerResourceEventHandlers<TResource extends ResourceType>(
      resourceName: ResourceName,
      resourceSet: (payload: ResourceSet<TResource>) => {
        payload: ResourceSet<TResource>;
        type: string;
      },
      resourceUpdated: (payload: ResourceUpdated) => {
        payload: ResourceUpdated;
        type: string;
      },
      resourceError: (payload: BoardEvent) => {
        payload: BoardEvent;
        type: string;
      },
      resourceErrorResolved: (payload: BoardEvent) => {
        payload: BoardEvent;
        type: string;
      },
    ): void {
      eventSource.addEventListener(`${resourceName}Set`, (e) => {
        handleBoardEvent<ResourceSet<TResource>>(e, (event) => {
          dispatch(resourceSet(event));
        });
      });

      eventSource.addEventListener(`${resourceName}Updated`, (e) => {
        handleBoardEvent<ResourceUpdated>(e, (event) => {
          dispatch(resourceUpdated(event));
        });
      });

      eventSource.addEventListener(`${resourceName}Error`, (e) => {
        handleBoardEvent<BoardEvent>(e, (event) => {
          dispatch(resourceError(event));
        });
      });

      eventSource.addEventListener(`${resourceName}ErrorResolved`, (e) => {
        handleBoardEvent<BoardEvent>(e, (event) => {
          dispatch(resourceErrorResolved(event));
        });
      });
    }

    function registerResourcesEventHandlers<TResource extends ResourcesType>(
      resourcesName: ResourcesName,
      resourcesSet: (payload: ResourcesSet<TResource>) => {
        payload: ResourcesSet<TResource>;
        type: string;
      },
      resourcesAdded: (payload: ResourcesAdded<TResource>) => {
        payload: ResourcesAdded<TResource>;
        type: string;
      },
      resourcesUpdated: (payload: ResourcesUpdated) => {
        payload: ResourcesUpdated;
        type: string;
      },
      resourcesRemoved: (payload: ResourcesRemoved) => {
        payload: ResourcesRemoved;
        type: string;
      },
      resourcesError: (payload: BoardEvent) => {
        payload: BoardEvent;
        type: string;
      },
      resourcesErrorResolved: (payload: BoardEvent) => {
        payload: BoardEvent;
        type: string;
      },
    ): void {
      eventSource.addEventListener(`${resourcesName}sSet`, (e) => {
        handleBoardEvent<ResourcesSet<TResource>>(e, (event) => {
          dispatch(resourcesSet(event));
        });
      });

      eventSource.addEventListener(`${resourcesName}sAdded`, (e) => {
        handleBoardEvent<ResourcesAdded<TResource>>(e, (event) => {
          dispatch(resourcesAdded(event));
        });
      });

      eventSource.addEventListener(`${resourcesName}sUpdated`, (e) => {
        handleBoardEvent<ResourcesUpdated>(e, (event) => {
          dispatch(resourcesUpdated(event));
        });
      });

      eventSource.addEventListener(`${resourcesName}sRemoved`, (e) => {
        handleBoardEvent<ResourcesRemoved>(e, (event) => {
          dispatch(resourcesRemoved(event));
        });
      });

      eventSource.addEventListener(`${resourcesName}sError`, (e) => {
        handleBoardEvent<BoardEvent>(e, (event) => {
          dispatch(resourcesError(event));
        });
      });

      eventSource.addEventListener(`${resourcesName}sErrorResolved`, (e) => {
        handleBoardEvent<BoardEvent>(e, (event) => {
          dispatch(resourcesErrorResolved(event));
        });
      });
    }

    const eventsUrl = createServerSentEventsUrl(
      boards,
      vehicleConnectionParameters,
      id,
    );
    if (!eventsUrl) {
      return;
    }

    const eventSource = new EventSource(eventsUrl);

    eventSource.addEventListener('open', () => {
      lastAliveMessageTime = Date.now();
    });

    eventSource.addEventListener('error', () => {
      // Retry if the event source is closed
      // This is meant to handle 404 and 500 response returned on server issues
      // This will unfortunately also catch 400 response on bad configuration, if this turns out to be an issue we could use @microsoft/fetch-event-source instead
      if (eventSource.readyState === EventSource.CLOSED) {
        setRetryConnectionTimer();
      }
    });

    eventSource.addEventListener('version', (e) => {
      hasMatchingVersion = e.data === VERSION;
      // Reload on non-matching version
      if (!hasMatchingVersion) {
        window.setTimeout(() => {
          location.reload();
        }, MILLISECONDS_OF_MAX_EXPECTED_SERVER_STARTUP_TIME);
      }
    });

    eventSource.addEventListener('message', () => {
      // Empty messages with no event field are used as alive message to know early if connection is lost
      lastAliveMessageTime = Date.now();
      dispatch(aliveEventReceived());
    });

    registerResourceEventHandlers(
      'stopHeader',
      stopHeaderSlice.actions.resourceSet,
      stopHeaderSlice.actions.resourceUpdated,
      stopHeaderSlice.actions.resourceError,
      stopHeaderSlice.actions.resourceErrorResolved,
    );
    registerResourceEventHandlers(
      'journey',
      journeySlice.actions.resourceSet,
      journeySlice.actions.resourceUpdated,
      journeySlice.actions.resourceError,
      journeySlice.actions.resourceErrorResolved,
    );
    registerResourcesEventHandlers(
      'departureRow',
      departureRowsSlice.actions.resourcesSet,
      departureRowsSlice.actions.resourcesAdded,
      departureRowsSlice.actions.resourcesUpdated,
      departureRowsSlice.actions.resourcesRemoved,
      departureRowsSlice.actions.resourcesError,
      departureRowsSlice.actions.resourcesErrorResolved,
    );
    registerResourcesEventHandlers(
      'arrivalRow',
      arrivalRowsSlice.actions.resourcesSet,
      arrivalRowsSlice.actions.resourcesAdded,
      arrivalRowsSlice.actions.resourcesUpdated,
      arrivalRowsSlice.actions.resourcesRemoved,
      arrivalRowsSlice.actions.resourcesError,
      arrivalRowsSlice.actions.resourcesErrorResolved,
    );
    registerResourcesEventHandlers(
      'trafficSituation',
      trafficSituationsSlice.actions.resourcesSet,
      trafficSituationsSlice.actions.resourcesAdded,
      trafficSituationsSlice.actions.resourcesUpdated,
      trafficSituationsSlice.actions.resourcesRemoved,
      trafficSituationsSlice.actions.resourcesError,
      trafficSituationsSlice.actions.resourcesErrorResolved,
    );
    registerResourcesEventHandlers(
      'extendedMessage',
      extendedMessagesSlice.actions.resourcesSet,
      extendedMessagesSlice.actions.resourcesAdded,
      extendedMessagesSlice.actions.resourcesUpdated,
      extendedMessagesSlice.actions.resourcesRemoved,
      extendedMessagesSlice.actions.resourcesError,
      extendedMessagesSlice.actions.resourcesErrorResolved,
    );

    const checkEventsAliveIntervalId = window.setInterval(() => {
      const millisecondsSinceLastAliveMessage =
        Date.now() - lastAliveMessageTime;
      if (
        millisecondsSinceLastAliveMessage >=
        MILLISECONDS_BETWEEN_ALIVE_MESSAGES +
          MILLISECONDS_OF_MAXIMUM_EXPECTED_LATENCY
      ) {
        retryConnection();
      }
    }, MILLISECONDS_BETWEEN_ALIVE_MESSAGES / 10);

    return () => {
      eventSource.close();
      window.clearTimeout(retryConnectionTimerId);
      window.clearInterval(checkEventsAliveIntervalId);
    };
  }, [boards, connectionErrorCount, dispatch, id, vehicleConnectionParameters]);
}
